import {
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/max';

import { countries } from '@frontend/features/common/constants/countries';

/**
 * Teléfonos de pasajero en formato internacional.
 *
 * Deja de ser cosmético con el contrato declarado: rail y passes EXIGEN el
 * teléfono del contacto para emitir la orden, y transferz lo exige para el
 * hold. El hub declara `phone` como string libre y lo pasa tal cual al
 * proveedor (DirectFerries lo parte en `+CC` + resto; Hotelbeds pide E.164):
 * el único guard es este. Lo que se persiste y viaja es siempre E.164
 * CANÓNICO — con el prefijo troncal nacional fuera: `096236320` en Uruguay es
 * `+59896236320`, no `+598096236320`, y eso solo se sabe por país. De ahí
 * libphonenumber-js: concatenar código + dígitos no es E.164.
 *
 * Metadata `/max`, no `/min`. El import por defecto de la librería —el que
 * usa `client-api` para WhatsApp/llamadas— equivale a `/min`: no trae las
 * regexps de rango por país, así que `isValid()` solo chequea el largo. Acá
 * eso deja pasar números con el largo correcto pero sin asignar (`UY`
 * `11111111`, 8 dígitos: `/min` lo da válido, `/max` no), y el campo exige
 * justamente que el rechazo lo capture el formulario, no el proveedor
 * después del hold. `/max` es ~65 KB más de bundle a cambio de esa garantía.
 */

export interface PhoneParts {
  /** ISO-2 del país elegido. Vacío = todavía sin elegir. */
  countryCode: string;
  /** Número nacional: dígitos, sin el código de país. */
  national: string;
}

const digitsOf = (value: string): string => value.replace(/\D/g, '');

const isPhoneCountry = (code: string): code is CountryCode =>
  isSupportedCountry(code);

/**
 * Países del selector: los habilitados del catálogo que libphonenumber conoce.
 * Los tres que no (GS, UM, PN) no tienen plan de numeración propio; afuera,
 * para que ningún país elegible falle al armar el número.
 */
export const PHONE_COUNTRIES = countries.filter(
  (country) => country.enable && isPhoneCountry(country.code),
);

/** Código de discado del país (`UY` → `598`); nada si no es un país válido. */
export function phoneCodeOf(countryCode: string): string | undefined {
  return isPhoneCountry(countryCode)
    ? getCountryCallingCode(countryCode)
    : undefined;
}

/**
 * El valor que se persiste. `undefined` mientras el número no sea válido para
 * el país elegido: un teléfono a medio tipear NO puede contar como campo
 * satisfecho, o el hold se habilita y el rechazo llega del proveedor. El
 * parseo por país saca el prefijo troncal (`0` en UY/AR, `011` en AR…) y
 * acepta también un internacional pegado entero (`+598…`) en el campo local.
 */
export function toE164(parts: PhoneParts): string | undefined {
  if (!isPhoneCountry(parts.countryCode)) return undefined;
  const parsed = parsePhoneNumberFromString(parts.national, parts.countryCode);
  return parsed?.isValid() ? parsed.number : undefined;
}

/**
 * Forma canónica de un valor ya internacional, o nada si no es un número
 * válido. `+598096236320` es válido pero NO canónico: canoniza a
 * `+59896236320`.
 */
export function canonicalE164(value: string | undefined): string | undefined {
  const trimmed = (value ?? '').trim();
  if (!trimmed.startsWith('+')) return undefined;
  const parsed = parsePhoneNumberFromString(trimmed);
  return parsed?.isValid() ? parsed.number : undefined;
}

/**
 * ¿Es exactamente lo que se le manda al proveedor? Válido para su país Y en
 * forma canónica. Es la regla de completitud de `hasPassengerFieldValue`:
 * el input siempre emite `toE164` (canónico por construcción), así que un
 * valor válido-pero-no-canónico solo puede venir de otra fuente y queda
 * insatisfecho hasta que el asesor lo toque.
 */
export function isValidE164(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return canonicalE164(trimmed) === trimmed;
}

/** `+59896236320` → `+598 96 236 320` para mostrar; nada si no es válido. */
export function formatInternational(value: string): string | undefined {
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.isValid() ? parsed.formatInternational() : undefined;
}

/**
 * Vuelve a partir un valor guardado para poder editarlo. libphonenumber
 * resuelve el país por rango cuando varios comparten código (+1: un 613 es
 * Canadá); si no puede (`+1 555…`), entre los del catálogo con ese código
 * gana el que ya estaba elegido. Los dígitos se preservan siempre — a lo sumo
 * se muestra la bandera de un vecino, nunca se pierde el número. Sin `+` no
 * hay país que inferir: dígitos y el país sugerido.
 */
export function parsePhone(
  value: string | undefined,
  preferredCountry = '',
): PhoneParts {
  const trimmed = (value ?? '').trim();
  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : undefined;
  if (!parsed) {
    return { countryCode: preferredCountry, national: digitsOf(trimmed) };
  }

  if (parsed.country) {
    return { countryCode: parsed.country, national: parsed.nationalNumber };
  }
  const sharing = PHONE_COUNTRIES.filter(
    (country) => phoneCodeOf(country.code) === parsed.countryCallingCode,
  );
  const chosen =
    sharing.find((country) => country.code === preferredCountry) ?? sharing[0];
  return {
    countryCode: chosen?.code ?? preferredCountry,
    national: parsed.nationalNumber,
  };
}

/**
 * País inicial del selector, derivado del idioma del navegador (`es-UY` → `UY`).
 * NO se hardcodea ninguno: la misma pantalla la usan agencias de países
 * distintos, y arrancar siempre en Uruguay hace que el resto tipee mal por
 * inercia. Sin región en el locale no se adivina: se elige a mano.
 */
export function defaultPhoneCountry(locale: string | undefined): string {
  const region = (locale ?? '').split('-')[1]?.toUpperCase();
  if (!region) return '';
  return PHONE_COUNTRIES.some((country) => country.code === region)
    ? region
    : '';
}
